import { InputHTMLAttributes, KeyboardEvent, SyntheticEvent } from 'react';
import cls from 'classnames';

import Button, { SIZE_XSMALL } from '../../Button/Button';
import Icon from '../../Icon/Icon';

import styles from './TextField.module.css';

export type TextFieldNormalize = (value: Nullable<StringOrNumber>) => unknown;

export type TextFieldChangeHandler = {
  bivarianceHack(event: SyntheticEvent<HTMLInputElement>, name?: string, normalize?: TextFieldNormalize): void;
}['bivarianceHack'];

const MODIFIER_CLASS_MAP: Record<string, string> = {
  squared: styles.formControlSquared,
  white: styles.formControlWhite,
  transparent: styles.formControlTransparent,
};

interface TextFieldProps extends Record<string, unknown> {
  value?: Nullable<StringOrNumber>;
  onChange?: Nullable<TextFieldChangeHandler>;
  onBlur?: Nullable<TextFieldChangeHandler>;
  className?: string;
  inputClassName?: string;
  name?: string;
  normalize?: Nullable<TextFieldNormalize>;
  prefix?: Nullable<string>;
  postfix?: Nullable<string>;
  modifier?: string;
  defaultValue?: Nullable<string>;
  onClear?: Nullable<() => void>;
  placeholder?: Nullable<string>;
  onKeyDown?: Nullable<(e: KeyboardEvent<HTMLInputElement>) => void>;
}

/** @deprecated use FancyInput */
function TextField(props: TextFieldProps) {
  const {
    value = undefined /* "undefined" is important here to ensure uncontrolled inputs behavior */,
    onChange = null,
    onBlur = null,
    className = '',
    inputClassName,
    name = '',
    normalize = null,
    prefix = '',
    postfix = '',
    modifier = '',
    defaultValue = null,
    onClear = null,
    placeholder = null,
    onKeyDown = null,
    ...rest
  } = props;

  let onChangeWrapper: Nullable<(evt: SyntheticEvent<HTMLInputElement>) => void> = null;

  if (normalize && onChange) {
    onChangeWrapper = (evt) => onChange(evt, name, normalize);
  } else if (onChange) {
    onChangeWrapper = (evt) => onChange(evt, name);
  }

  let onBlurWrapper: Nullable<(evt: SyntheticEvent<HTMLInputElement>) => void> = null;

  if (normalize && onBlur) {
    onBlurWrapper = (evt) => onBlur(evt, name, normalize);
  } else if (onBlur) {
    onBlurWrapper = (evt) => onBlur(evt, name);
  }

  function handleKeyPress(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') {
      return;
    }

    if (onChangeWrapper) {
      onChangeWrapper(e);
    }

    if (onBlurWrapper) {
      onBlurWrapper(e);
    }
  }

  let sanitizedValue = value;

  if ((value === null || value === undefined) && defaultValue === null) {
    sanitizedValue = '';
  }

  const inputProps: Record<string, unknown> = {
    className: cls(
      'form-control',
      modifier ? MODIFIER_CLASS_MAP[modifier] : '',
      prefix ? styles.formControlPrefixed : '',
      postfix ? styles.formControlPostfixed : '',
      inputClassName,
    ),
    name: name || '',
    ...rest,
  };

  if (onBlurWrapper) {
    inputProps.defaultValue = sanitizedValue;
    inputProps.onBlur = onBlurWrapper;
  }

  if (onChangeWrapper) {
    inputProps.value = sanitizedValue;
    inputProps.onChange = onChangeWrapper;
  }

  return (
    <div className={cls(styles.formControlWrapper, className)}>
      {prefix && <span className={styles.formControlPrefix}>{prefix}</span>}

      {onClear && value && (
        <div className={styles.textFieldCloseButtonWrapper}>
          <Button aria-label="Clear input" color="transparent" onClick={onClear} size={SIZE_XSMALL} withIcon>
            <Icon name="close" />
          </Button>
        </div>
      )}

      <input
        key={onBlurWrapper ? `${sanitizedValue}` : undefined}
        {...(inputProps as InputHTMLAttributes<HTMLInputElement>)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder ?? undefined}
        onKeyDown={onKeyDown ?? undefined}
      />

      {postfix && <span className={styles.formControlPostfix}>{postfix}</span>}
    </div>
  );
}

export default TextField;
